import { useCallback, useEffect, useRef, useState } from "react";

export function useAsync<T>(loader: () => Promise<T>, dependencies: unknown[] = []) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const mounted = useRef(true);
  const run = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const result = await loader();
      if (mounted.current) setData(result);
      return result;
    } catch (caught) {
      if (mounted.current) setError(caught instanceof Error ? caught.message : "Произошла ошибка");
      return null;
    } finally {
      if (mounted.current && !silent) setLoading(false);
    }
  }, dependencies);
  useEffect(() => {
    mounted.current = true;
    void run();
    return () => { mounted.current = false; };
  }, [run]);
  const refresh = useCallback(() => run(true), [run]);
  return { data, error, loading, reload: run, refresh, setData };
}

export function useDebouncedEffect(effect: () => void | (() => void), delay: number, dependencies: unknown[]) {
  useEffect(() => {
    const timer = window.setTimeout(effect, delay);
    return () => window.clearTimeout(timer);
  }, [...dependencies, delay]);
}

/** Refreshes live admin data only while its page is visible. */
export function useAutoRefresh(reload: () => Promise<unknown>, intervalMs = 1000) {
  const reloadRef = useRef(reload);
  useEffect(() => {
    reloadRef.current = reload;
  }, [reload]);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === "visible") void reloadRef.current();
    };
    const timer = window.setInterval(refresh, intervalMs);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [intervalMs]);
}

